import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { submissionsAPI, bulkAPI } from '../utils/api';
import { format } from 'date-fns';
import { canManageUsers } from '../utils/permissions';
import CSVImportModal from '../components/CSVImportModal';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkActionReason, setBulkActionReason] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [submissionTypeFilter, setSubmissionTypeFilter] = useState('all'); // 'all', 'text_giving', 'messaging'

  // Memoize loadData to prevent unnecessary re-renders
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        status: filter === 'all' ? undefined : filter,
        submission_type: submissionTypeFilter === 'all' ? undefined : submissionTypeFilter
      };

      // Handle archived filter
      if (includeArchived) {
        filters.include_archived = true; // Show BOTH active and archived
      } else {
        // Default: show only active (is_archived = false)
      }

      // Pass includeArchived to stats API as well so counts update
      const statsFilters = includeArchived ? { include_archived: true } : {};

      const [submissionsRes, statsRes] = await Promise.all([
        submissionsAPI.getAll(filters),
        submissionsAPI.getStats(statsFilters)
      ]);

      setSubmissions(submissionsRes.data.submissions);
      setStats(statsRes.data.stats);
    } catch (error) {
      console.error('❌ Failed to load data:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [filter, includeArchived, submissionTypeFilter, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      loadData();
      return;
    }

    try {
      const response = await submissionsAPI.search(searchQuery);
      setSubmissions(response.data.submissions);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedSubmissions = () => {
    let filtered = [...submissions];

    // Apply date filtering
    if (dateFrom) {
      filtered = filtered.filter(sub => new Date(sub.created_at) >= new Date(dateFrom));
    }
    if (dateTo) {
      filtered = filtered.filter(sub => new Date(sub.created_at) <= new Date(dateTo + 'T23:59:59'));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'created_at' || sortField === 'updated_at' || sortField === 'submitted_to_twilio_at') {
        // Handle null dates by treating them as very old (for ascending) or very new (for descending)
        if (!aVal && !bVal) return 0;
        if (!aVal) return sortDirection === 'asc' ? -1 : 1;
        if (!bVal) return sortDirection === 'asc' ? 1 : -1;

        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const exportToCSV = () => {
    const sortedData = getSortedSubmissions();

    const headers = [
      'ID',
      'Organization',
      'Contact Name',
      'Contact Email',
      'Contact Phone',
      'Phone Number',
      'Phone Type',
      'Status',
      'Twilio Status',
      'Zendesk Ticket ID',
      'Created At',
      'Last Submitted to Twilio',
      'Updated At'
    ];

    const rows = sortedData.map(sub => [
      sub.submission_uuid,
      sub.organization_name,
      sub.contact_name,
      sub.contact_email,
      sub.contact_phone || '',
      sub.phone_number,
      sub.phone_number_type,
      sub.status,
      sub.twilio_status || 'N/A',
      sub.zendesk_ticket_id || '',
      format(new Date(sub.created_at), 'yyyy-MM-dd HH:mm:ss'),
      sub.submitted_to_twilio_at ? format(new Date(sub.submitted_to_twilio_at), 'yyyy-MM-dd HH:mm:ss') : '',
      format(new Date(sub.updated_at), 'yyyy-MM-dd HH:mm:ss')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `submissions-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = getSortedSubmissions().map(sub => sub.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkAction = () => {
    if (!bulkAction) {
      toast.warning('Please select an action');
      return;
    }
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one submission');
      return;
    }
    setShowBulkModal(true);
  };

  const executeBulkAction = async () => {
    setBulkProcessing(true);
    try {
      let result;
      if (bulkAction === 'submit_to_twilio') {
        result = await bulkAPI.submitToTwilio(selectedIds);
      } else if (bulkAction === 'archive' || bulkAction === 'unarchive') {
        // New archive handling
        const is_archived = bulkAction === 'archive';
        result = await bulkAPI.archive(selectedIds, is_archived, bulkActionReason);
      } else {
        result = await bulkAPI.updateStatus(selectedIds, bulkAction, bulkActionReason);
      }

      // Handle different response formats
      let successCount = 0;
      let failedCount = 0;

      if (bulkAction === 'submit_to_twilio') {
        // submit_to_twilio returns: { submitted, awaiting_approval, failed }
        const { submitted = [], awaiting_approval = [], failed = [] } = result.data.results || {};
        successCount = submitted.length + awaiting_approval.length;
        failedCount = failed.length;

        if (awaiting_approval.length > 0) {
          toast.info(`${awaiting_approval.length} submission(s) awaiting Customer Profile approval`);
        }
      } else {
        // updateStatus and archive return: { success, failed }
        const { success = [], failed = [] } = result.data.results || {};
        successCount = success.length;
        failedCount = failed.length;
      }

      toast.success(`Bulk action completed! Success: ${successCount}, Failed: ${failedCount}`);

      // Refresh data and reset
      await loadData();
      setSelectedIds([]);
      setBulkAction('');
      setBulkActionReason('');
      setShowBulkModal(false);
    } catch (error) {
      console.error('Bulk action failed:', error);
      toast.error('Failed to execute bulk action: ' + (error.response?.data?.error || error.message));
    } finally {
      setBulkProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_review: 'badge-pending',
      in_review: 'badge-in-progress',
      ready_to_submit: 'badge-in-progress',
      awaiting_profile_approval: 'badge-in-progress',
      submitted_to_twilio: 'badge-in-progress',
      awaiting_twilio: 'badge-in-progress',
      approved: 'badge-approved',
      rejected: 'badge-rejected',
      resubmitted: 'badge-in-progress'
    };

    return (
      <span className={`badge ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
      </span>
    );
  };

  const getTwilioStatusBadge = (twilioStatus) => {
    if (!twilioStatus) {
      return <span className="text-sm text-gray-400">—</span>;
    }

    // Map Twilio statuses to badge styles and human-readable text
    const statusConfig = {
      'PENDING_REVIEW': { badge: 'badge-pending', label: 'Pending Review' },
      'IN_REVIEW': { badge: 'badge-in-progress', label: 'In Review' },
      'TWILIO_APPROVED': { badge: 'badge-approved', label: 'Approved' },
      'TWILIO_REJECTED': { badge: 'badge-rejected', label: 'Rejected' },
      'PROVISIONALLY_APPROVED': { badge: 'badge-approved', label: 'Provisionally Approved' }
    };

    const config = statusConfig[twilioStatus] || {
      badge: 'bg-gray-100 text-gray-800',
      label: twilioStatus.replace(/_/g, ' ')
    };

    return (
      <span className={`badge ${config.badge}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-[90%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tithely Verification Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {user?.name}</p>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/analytics" className="btn btn-outline text-sm">
                Analytics
              </Link>
              <Link to="/email-templates" className="btn btn-outline text-sm">
                Email Templates
              </Link>
              <Link to="/submission-configs" className="btn btn-outline text-sm">
                Submission Configs
              </Link>
              {canManageUsers(user?.role) && (
                <Link to="/users" className="btn btn-outline text-sm">
                  Manage Users
                </Link>
              )}
              <button onClick={logout} className="btn btn-secondary text-sm">
                Logout
              </button>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden btn btn-secondary p-2"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col gap-2">
                <Link to="/analytics" className="btn btn-outline w-full text-left" onClick={() => setShowMobileMenu(false)}>
                  Analytics
                </Link>
                <Link to="/email-templates" className="btn btn-outline w-full text-left" onClick={() => setShowMobileMenu(false)}>
                  Email Templates
                </Link>
                <Link to="/submission-configs" className="btn btn-outline w-full text-left" onClick={() => setShowMobileMenu(false)}>
                  Submission Configs
                </Link>
                {canManageUsers(user?.role) && (
                  <Link to="/users" className="btn btn-outline w-full text-left" onClick={() => setShowMobileMenu(false)}>
                    Manage Users
                  </Link>
                )}
                <button onClick={() => { logout(); setShowMobileMenu(false); }} className="btn btn-secondary w-full text-left">
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-[90%] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats - Reordered: Ready for Review, Submitted to Twilio, Approved, Rejected */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* 1. Ready for Review Block - Highest Priority */}
            {stats.ready_for_review_count !== undefined && (
              <button
                onClick={() => setFilter('pending_review')}
                className={`card text-left hover:shadow-lg transition-all cursor-pointer border-2 ${
                  filter === 'pending_review'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-transparent hover:border-yellow-200'
                }`}
              >
                <p className="text-sm text-gray-600 mb-1">Ready For Review</p>
                <p className="text-3xl font-bold text-gray-900">{stats.ready_for_review_count}</p>
                <p className="text-xs text-yellow-600 mt-1">⚠️ Needs Review</p>
              </button>
            )}

            {/* 2-4. Status Blocks in Order: Submitted to Twilio, Approved, Rejected */}
            {['submitted_to_twilio', 'approved', 'rejected']
              .map(statusKey => stats.by_status.find(stat => stat.status === statusKey))
              .filter(stat => stat)
              .map((stat) => {
                const isSelected = filter === stat.status;
                const weekChange = stat.week_over_week_change || 0;
                const changeColor = weekChange > 0 ? 'text-green-600' : weekChange < 0 ? 'text-red-600' : 'text-gray-500';
                const changeIcon = weekChange > 0 ? '↑' : weekChange < 0 ? '↓' : '→';

                return (
                  <button
                    key={stat.status}
                    onClick={() => setFilter(stat.status)}
                    className={`card text-left hover:shadow-lg transition-all cursor-pointer border-2 ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-transparent hover:border-primary-200'
                    }`}
                  >
                    <p className="text-sm text-gray-600 mb-1">
                      {stat.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">{stat.count}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs font-medium ${changeColor}`}>
                        {changeIcon} {Math.abs(weekChange)}%
                      </span>
                      <span className="text-xs text-gray-500">vs last week</span>
                    </div>
                  </button>
                );
              })}
          </div>
        )}

        {/* Filters and Search */}
        <div className="card mb-6">
          <div className="space-y-4">
            <div className="flex flex-col gap-4">
              {/* Status Filter Buttons with Counts */}
              <div className="flex gap-2 flex-wrap w-full items-center">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filter === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All {stats && `(${stats.by_status.reduce((sum, stat) => sum + stat.count, 0)})`}
                </button>
                <button
                  onClick={() => setFilter('pending_review')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filter === 'pending_review'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Pending Review {stats && `(${stats.by_status.find(s => s.status === 'pending_review')?.count || 0})`}
                </button>
                <button
                  onClick={() => setFilter('ready_to_submit')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filter === 'ready_to_submit'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Ready to Submit {stats && `(${stats.by_status.find(s => s.status === 'ready_to_submit')?.count || 0})`}
                </button>
                <button
                  onClick={() => setFilter('submitted_to_twilio')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filter === 'submitted_to_twilio'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Submitted to Twilio {stats && `(${stats.by_status.find(s => s.status === 'submitted_to_twilio')?.count || 0})`}
                </button>
                <button
                  onClick={() => setFilter('approved')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filter === 'approved'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Approved {stats && `(${stats.by_status.find(s => s.status === 'approved')?.count || 0})`}
                </button>
                <button
                  onClick={() => setFilter('rejected')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filter === 'rejected'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Rejected {stats && `(${stats.by_status.find(s => s.status === 'rejected')?.count || 0})`}
                </button>

                {/* Date Filters Button - Moved from search area */}
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="btn btn-secondary whitespace-nowrap ml-auto"
                >
                  {showAdvancedFilters ? 'Hide' : 'Show'} Date Filters
                </button>
              </div>

              {/* Search and Archive Toggle Section */}
              <div className="flex flex-col lg:flex-row gap-3 w-full items-stretch lg:items-center">
                {/* Left: Search */}
                <form onSubmit={handleSearch} className="flex gap-2 flex-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search submissions..."
                    className="input flex-1 min-w-0"
                  />
                  <button type="submit" className="btn btn-primary whitespace-nowrap">
                    Search
                  </button>
                </form>

                {/* Right: Archive Toggle */}
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    id="include-archived"
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Include archived submissions in results"
                  />
                  <label
                    htmlFor="include-archived"
                    className="text-sm font-medium text-gray-700 cursor-pointer select-none whitespace-nowrap"
                  >
                    Include Archived
                    {stats?.archived_count !== undefined && (
                      <span className="ml-1 text-gray-500">({stats.archived_count})</span>
                    )}
                  </label>
                </div>

                {/* Submission Type Filter */}
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Type:
                  </label>
                  <select
                    value={submissionTypeFilter}
                    onChange={(e) => setSubmissionTypeFilter(e.target.value)}
                    className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Types</option>
                    <option value="text_giving">Text Giving</option>
                    <option value="messaging">Messaging</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Date Range</h3>
                <div className="flex gap-4 items-center">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="input"
                    />
                  </div>
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="btn btn-secondary mt-5"
                  >
                    Clear Dates
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.length > 0 && (
          <div className="card mb-6 bg-primary-50 border-primary-200">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                {selectedIds.length} selected
              </span>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="input flex-1 sm:max-w-xs"
              >
                <option value="">Select Action...</option>
                <option value="ready_to_submit">Mark as Ready to Submit</option>
                <option value="submit_to_twilio">Submit to Twilio</option>
                <option value="in_review">Mark as In Review</option>
                <option value="rejected">Mark as Rejected</option>
                {includeArchived ? (
                  <option value="unarchive">Restore from Archive</option>
                ) : (
                  <option value="archive">Archive</option>
                )}
              </select>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkAction}
                  disabled={!bulkAction}
                  className="btn btn-primary flex-1 sm:flex-none whitespace-nowrap"
                >
                  Execute Action
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="btn btn-secondary flex-1 sm:flex-none whitespace-nowrap"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submissions List */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Submissions</h2>
            {/* Import/Export Controls */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import Submissions
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
                title="Export to CSV"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading submissions...</p>
            </div>
          ) : getSortedSubmissions().length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                No submissions found
                {filter !== 'all' && ' with the current filters'}
              </p>
              {filter !== 'all' && (
                <button
                  onClick={() => {
                    setFilter('all');
                  }}
                  className="mt-4 btn btn-primary"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === getSortedSubmissions().length && getSortedSubmissions().length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </th>
                    <th
                      onClick={() => handleSort('organization_name')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Organization
                        {sortField === 'organization_name' && (
                          <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('submission_type')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortField === 'submission_type' && (
                          <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('phone_number')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Phone Number
                        {sortField === 'phone_number' && (
                          <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('status')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortField === 'status' && (
                          <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('twilio_status')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Twilio Status
                        {sortField === 'twilio_status' && (
                          <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('created_at')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Created
                        {sortField === 'created_at' && (
                          <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('submitted_to_twilio_at')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Last Submitted
                        {sortField === 'submitted_to_twilio_at' && (
                          <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedSubmissions().map((submission) => (
                    <tr
                      key={submission.id}
                      className={`cursor-pointer transition-colors ${
                        submission.is_archived
                          ? 'bg-gray-50 hover:bg-gray-100 opacity-75'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={(e) => {
                        // Don't navigate if clicking checkbox or button
                        if (!e.target.closest('input[type="checkbox"]') && !e.target.closest('button')) {
                          navigate(`/submissions/${submission.id}`);
                        }
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(submission.id)}
                          onChange={() => handleSelectOne(submission.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                              {submission.organization_name}
                              {submission.is_archived && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700"
                                  title="This submission is archived"
                                >
                                  📦 Archived
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{submission.contact_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          submission.submission_type === 'messaging'
                            ? 'bg-blue-100 text-blue-800'
                            : submission.submission_type === 'text_giving'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}>
                          {submission.submission_type === 'messaging'
                            ? 'Messaging'
                            : submission.submission_type === 'text_giving'
                              ? 'Text Giving'
                              : submission.submission_type || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {submission.phone_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(submission.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTwilioStatusBadge(submission.twilio_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(submission.created_at), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {submission.submitted_to_twilio_at
                          ? (<span>{format(new Date(submission.submitted_to_twilio_at), 'MMM d, yyyy')}</span>)
                          : (<span className="text-gray-400">—</span>)
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium actions-column">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/submissions/${submission.id}`);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bulk Action Confirmation Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Bulk Action</h3>

              <p className="text-gray-600 mb-4">
                You are about to <strong>
                  {bulkAction === 'submit_to_twilio'
                    ? 'submit to Twilio'
                    : bulkAction === 'archive'
                    ? 'archive (status will be preserved)'
                    : bulkAction === 'unarchive'
                    ? 'restore from archive'
                    : `update status to ${bulkAction}`}
                </strong> for {selectedIds.length} submission{selectedIds.length > 1 ? 's' : ''}.
              </p>

              {bulkAction !== 'submit_to_twilio' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason (optional)
                  </label>
                  <textarea
                    value={bulkActionReason}
                    onChange={(e) => setBulkActionReason(e.target.value)}
                    rows={3}
                    className="input w-full"
                    placeholder={bulkAction === 'archive' || bulkAction === 'unarchive'
                      ? "Enter reason for this action..."
                      : "Enter reason for status change..."}
                  />
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowBulkModal(false)}
                  disabled={bulkProcessing}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={executeBulkAction}
                  disabled={bulkProcessing}
                  className="btn btn-primary"
                >
                  {bulkProcessing ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSV Import Modal */}
        <CSVImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={async () => {
            try {
              await loadData();
              toast.success('CSV import completed successfully!');
            } catch (err) {
              toast.error('CSV import succeeded, but failed to refresh data.');
            }
          }}
        />
      </div>
    </div>
  );
}
